import { z } from "zod";
import type { PermissionKey } from "../permissions";

/**
 * Plain-language causes and next steps for import failures (product gaps row 4, design
 * `docs/research/2026-09-28-design-failure-causes.md`).
 *
 * A cause is a SOURCE and a CLOSED CODE, never text (SEC-17). The runner decides the code where it
 * reads the raw error (`runner/steps/importCause.ts`) and stores only the code; the server turns a
 * code into words (`server/services/importCauseView.ts`); the client never composes a sentence.
 *
 * Three sources belong to an import: `connector` (the extract), `load` (the warehouse load) and
 * `platform` (the job itself: it never started, ran out of time or was lost). The fourth source a
 * notification carries, `dbt`, keeps its own table in `dbtCauses.ts`.
 *
 * Each code has a label (a badge), a sentence (what happened), a fix (one line: what to do) and one
 * action from `IMPORT_CAUSE_ACTIONS`, which the client turns into a button when the reader holds the
 * action's permission. The nine text categories stay, unchanged in meaning, as the fallback tier:
 * old runs and old outbox rows keep parsing.
 *
 * `shared/` must not import `notifications.ts` here: that module imports this one.
 */

/** The nine categories `categoriseErrorText` answers (`runner/steps/sanitiseError.ts`). */
export const TEXT_CAUSE_CODES = [
  "timeout",
  "auth",
  "connection",
  "data_type",
  "contention",
  "missing_object",
  "resource",
  "syntax",
  "unknown",
] as const;
export type TextCauseCode = (typeof TEXT_CAUSE_CODES)[number];

/** A failed extract (source `connector`): the text tier, then the specific codes. */
export const EXTRACT_CAUSE_CODES = [
  ...TEXT_CAUSE_CODES,
  "source_login_rejected",
  "sql_sign_in_rejected",
  "source_secret_unreadable",
  "source_unreachable",
  "source_database_offline",
  "source_database_not_opened",
  "source_throttled",
  "source_config_invalid",
  "source_host_key_changed",
  "table_permission_denied",
  "table_missing",
  "column_missing",
  "object_name_unreadable",
  "source_cross_database_denied",
  "view_query_failed",
  "column_type_unsupported",
  "file_parse_failed",
  "read_interrupted",
  // GA4 and Google Search Console (#1931): the connector has no password to update.
  "google_access_not_granted",
  "google_sign_in_failed",
  "google_setup_incomplete",
  "google_service_account_invalid",
  // #2428: a GA4 report a person added: Google refused its fields, or it has no definition.
  "report_definition_invalid",
  "report_definition_missing",
] as const;
export type ExtractCauseCode = (typeof EXTRACT_CAUSE_CODES)[number];

/** A failed load (source `load`). */
export const LOAD_CAUSE_CODES = [
  ...TEXT_CAUSE_CODES,
  "stage_credential_rejected",
  "warehouse_login_rejected",
  // #1730 review: 390189, the Role in Destination does not exist or is not granted to the user.
  "warehouse_role_rejected",
  "warehouse_unavailable",
  "warehouse_object_denied",
  "column_narrowed",
  "rows_short",
  "load_mode_mismatch",
  "table_name_conflict",
  "push_failed",
  "stage_conflict",
  "load_not_ready",
  "destination_incomplete",
  "target_not_ours",
  "old_column_names",
] as const;
export type LoadCauseCode = (typeof LOAD_CAUSE_CODES)[number];

/** A failure of the job rather than of the source or the warehouse (source `platform`). */
export const PLATFORM_CAUSE_CODES = [
  "connection_not_ready",
  "job_not_started",
  "run_timed_out",
  "job_lost",
  "run_overlapped",
  "connection_untested",
  "tenant_disabled",
  "tenant_not_ready",
] as const;
export type PlatformCauseCode = (typeof PLATFORM_CAUSE_CODES)[number];

export const IMPORT_CAUSE_SOURCES = ["connector", "load", "platform"] as const;
export type ImportCauseSource = (typeof IMPORT_CAUSE_SOURCES)[number];

/** The closed set of buttons a cause offers. The client maps each to a screen or a dialog. */
export const IMPORT_CAUSE_ACTIONS = [
  "open_connection",
  "validate_connection",
  "open_tables",
  "open_destination",
  "retry_failed_tables",
  "resync_table",
  "full_refresh",
  "copy_reference",
] as const;
export type ImportCauseAction = (typeof IMPORT_CAUSE_ACTIONS)[number];

export interface ImportCauseEntry {
  /** A short badge, two to five words. */
  label: string;
  /** What happened, one sentence without a full stop (the notification line adds the code). */
  sentence: string;
  /** What to do, one line. */
  fix: string;
  action: ImportCauseAction;
  /**
   * #1659: the line a reader without the action's key reads, when the fix is made outside the
   * action's page (a grant in Snowflake). Absent, the action's own `withoutPermission`.
   */
  askLine?: string;
  /**
   * True when the fix sends the reader to a firewall: the server appends the platform's outbound
   * address (`firewallSentence`) when the web could read it, and "ask Skypoint support" otherwise.
   */
  firewall?: true;
}

/**
 * What a reader without `instance.binding.write` reads for a Destination fix (#1659). The key is
 * PlatformAdmin-only (`shared/permissions.ts`, docs/rbac.md), so no tenant role can make the
 * change and no reader is sent to a tenant admin for it.
 */
export const ASK_SKYPOINT_DESTINATION = "Ask Skypoint support to make this change in Destination.";

/**
 * #1659: the line for a cause fixed in the customer's Snowflake account, not in Destination. A
 * reader without the Destination key reads who can make the grant, and that a wrong name in
 * Destination is Skypoint's to change.
 */
export const ASK_SNOWFLAKE_ADMIN =
  "Ask whoever manages your Snowflake account to make this grant. If the name in Destination is wrong, ask Skypoint support to change it.";

/** The ask line of a stage clash: the fix is a drop or rename in the customer's Snowflake. */
export const ASK_SNOWFLAKE_STAGE =
  "Ask whoever manages your Snowflake account to drop or rename that stage. If the name in Destination is wrong, ask Skypoint support to change it.";

/**
 * The button each action draws, and the permission it needs (the routes the action calls check the
 * same keys; `docs/rbac.md`). `withoutPermission` is what a reader without the key reads instead of
 * the button. `instance.binding.write` is PlatformAdmin-only (`shared/permissions.ts`), so a
 * Destination fix is Skypoint's for every other reader, a tenant admin included (#1659).
 */
export const IMPORT_CAUSE_ACTION_META = {
  open_connection: {
    label: "Open the connection",
    permission: "dataflows.write",
    withoutPermission: null,
  },
  // The client only navigates for this one, `resync_table` and `full_refresh`, so each label
  // names the page it opens and what to do there (review r1 N2).
  validate_connection: {
    label: "Open the connection to validate it",
    permission: "dataflows.validate",
    withoutPermission: null,
  },
  open_tables: {
    label: "Open the connection's tables",
    permission: "dataflows.write",
    withoutPermission: null,
  },
  open_destination: {
    label: "Open Destination",
    permission: "instance.binding.write",
    withoutPermission: ASK_SKYPOINT_DESTINATION,
  },
  retry_failed_tables: {
    label: "Retry the failed tables",
    permission: "runs.retry",
    withoutPermission: null,
  },
  resync_table: {
    label: "Open the tables to re-sync",
    permission: "runs.start",
    withoutPermission: null,
  },
  // Owner 2026-10-31 (`old_column_names`): every connection kind, a report connection included,
  // has Import with options and its Full refresh on the connection's page; a report list has no
  // per-table re-sync, so this does not open the tables.
  full_refresh: {
    label: "Open the connection to run a full refresh",
    permission: "runs.start",
    withoutPermission: null,
  },
  copy_reference: { label: "Copy the reference", permission: null, withoutPermission: null },
} as const satisfies Record<
  ImportCauseAction,
  { label: string; permission: PermissionKey | null; withoutPermission: string | null }
>;

/**
 * #1084: the step that lists a connection's tables (and their columns) again, named as a step and
 * never by a button's label: the editor's button reads "Validate connection", or "Re-validate"
 * once a credential is stored (`client/src/components/connections/SaveBar.tsx`), and some
 * connectors never store one. `client/src/components/connections/__tests__/validateStep.test.ts`
 * holds the two in step.
 */
export const VALIDATE_AGAIN_STEP = "validate the connection again to list them";

const RETRY = "Retry the failed tables.";
const SUPPORT = "Open the run's events. Send the reference to support if it repeats.";

/** The nine text categories on an extract. The sentences are the notification contract's (§3.2). */
const CONNECTOR_TEXT: Record<TextCauseCode, ImportCauseEntry> = {
  timeout: {
    label: "Source timed out",
    sentence: "The source did not answer in time",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  auth: {
    label: "Sign-in refused",
    sentence: "The source refused the sign-in",
    fix: "Check the credential on the connection, then validate it.",
    action: "validate_connection",
  },
  connection: {
    label: "Source unreachable",
    sentence: "The source could not be reached",
    fix: "Check the host and port on the connection.",
    action: "open_connection",
    firewall: true,
  },
  data_type: {
    label: "Value did not fit",
    sentence: "A value could not be converted to its column type",
    fix: "A source value did not fit its declared type. Check the column's type on the connection.",
    action: "open_tables",
  },
  contention: {
    label: "Source busy",
    sentence: "The source was busy with a lock or a deadlock",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  missing_object: {
    label: "Table or column missing",
    sentence: "A selected table or column no longer exists at the source",
    // #1084: the same next step as the extract's table_missing, for a table or a column.
    fix: `Check that the table or column exists in the source. If it was renamed or removed, open the connection's tables, ${VALIDATE_AGAIN_STEP} and update your selection.`,
    action: "open_tables",
  },
  resource: {
    label: "Source out of resources",
    sentence: "The source ran out of a resource or was throttled",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  syntax: {
    label: "Query not accepted",
    sentence: "The source did not accept a query",
    fix: "Send the reference to support.",
    action: "copy_reference",
  },
  unknown: {
    label: "Not recognised",
    sentence: "The cause was not recognised. The run page has the details",
    fix: SUPPORT,
    action: "copy_reference",
  },
};

/** The nine text categories on a load. */
const LOAD_TEXT: Record<TextCauseCode, ImportCauseEntry> = {
  timeout: {
    label: "Warehouse timed out",
    sentence: "Loading into the warehouse timed out",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  auth: {
    label: "Warehouse refused",
    sentence: "The warehouse refused the sign-in",
    fix: "Check the sign-in and the role in Destination.",
    action: "open_destination",
  },
  connection: {
    label: "Warehouse unreachable",
    sentence: "The warehouse could not be reached",
    fix: "Check the account in Destination, then retry the failed tables.",
    action: "open_destination",
  },
  data_type: {
    label: "Value did not fit",
    sentence: "A value could not be loaded into its column type",
    fix: "A value did not fit its target column. Check the column's type on the connection.",
    action: "open_tables",
  },
  contention: {
    label: "Warehouse busy",
    sentence: "The warehouse was busy with a lock",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  missing_object: {
    label: "Object missing",
    sentence: "A table or stage the load needs does not exist",
    fix: "Check that the database, schema and stage in Destination exist.",
    action: "open_destination",
    askLine: ASK_SNOWFLAKE_ADMIN,
  },
  resource: {
    label: "Warehouse out of resources",
    sentence: "The warehouse ran out of a resource or was throttled",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  syntax: {
    label: "Statement not accepted",
    sentence: "The warehouse did not accept a load statement",
    fix: "Send the reference to support.",
    action: "copy_reference",
  },
  unknown: {
    label: "Not recognised",
    sentence: "The cause was not recognised. The run page has the details",
    fix: SUPPORT,
    action: "copy_reference",
  },
};

const EXTRACT_SPECIFIC: Record<Exclude<ExtractCauseCode, TextCauseCode>, ImportCauseEntry> = {
  source_login_rejected: {
    label: "Sign-in refused",
    sentence: "The source refused the sign-in skyData uses",
    fix: "Update the password on the connection, then validate it.",
    action: "validate_connection",
  },
  // #1097: SQL Server and MySQL only (`runner/steps/importCause.ts` `sqlSignInCause`). A wrong
  // database name or port can be refused as a sign-in, so the words name every field it can mean.
  sql_sign_in_rejected: {
    label: "Sign in or settings refused",
    sentence: "The source refused the sign-in, or the database name or port is wrong",
    fix: "Check the user name, password, database name and port on the connection, then validate it.",
    action: "validate_connection",
  },
  source_secret_unreadable: {
    label: "Credential not readable",
    sentence: "skyData could not read the connection's stored credential",
    fix: "Enter the credential again on the connection. If it keeps failing, send the reference to support.",
    action: "open_connection",
  },
  source_unreachable: {
    label: "Source unreachable",
    sentence: "The source could not be reached from skyData",
    fix: "Check the host and port on the connection.",
    action: "open_connection",
    firewall: true,
  },
  source_database_offline: {
    label: "Database offline",
    sentence: "The server answered but the database was offline or unavailable",
    fix: "Retry the failed tables when the database is back.",
    action: "retry_failed_tables",
  },
  // #1158: SQL Server 4060 or 4064, MySQL 1049, before this run (or the run it retries) read any
  // table of the connection. One answer means a wrong name, a login with no access to the
  // database, or a database offline at sign-in, so the words name all three.
  source_database_not_opened: {
    label: "Database not opened",
    sentence:
      "The server answered but could not open the database, so the name may be wrong, this user may not have access to it, or it may be offline",
    fix: "Check the database name and this user's access to it, then validate the connection. If the database is offline, retry the failed tables when it is back.",
    action: "validate_connection",
  },
  source_throttled: {
    label: "Source throttled",
    sentence: "The source asked skyData to slow down",
    fix: "Retry the failed tables later, or lower the connection's parallel reads.",
    action: "retry_failed_tables",
  },
  source_config_invalid: {
    label: "Settings not usable",
    sentence: "The connection's settings could not be used to start the read",
    fix: "Check the connection's settings, then validate it.",
    action: "validate_connection",
  },
  // #1931, GA4 and Google Search Console. The run signs in to Google as the tenant's own service
  // account through Skypoint's identity binding, so "update the password" is never the fix.
  // A 403 on a property or site: the customer has not granted that service account access.
  google_access_not_granted: {
    // The owner's words ("Grant access to ... service account") lead the fix, naming the
    // tenant's account, never Skypoint's shared one: a label is at most five words.
    label: "Google access not granted",
    sentence: "Google refused the connection's service account access to a listed property or site",
    fix: "Grant access to the service account Skypoint set up for you. Add the service account named on the connection as a Viewer on each GA4 property, or as a user on each Search Console site, then validate the connection.",
    action: "validate_connection",
  },
  // The identity exchange with Google failed: Skypoint's binding for this tenant, not a setting.
  google_sign_in_failed: {
    label: "Google sign-in failed at Skypoint",
    sentence: "Skypoint could not sign in to Google for this connection",
    fix: "Send the reference to support. Nothing on the connection needs to change.",
    action: "copy_reference",
  },
  // A Google API switched off on Skypoint's Google Cloud project: Skypoint's setup, as the hub's
  // API_NOT_ENABLED on Validate (`shared/connectors/googleFaults.ts`).
  google_setup_incomplete: {
    label: "Google setup incomplete",
    sentence: "Skypoint's Google setup for this service is incomplete",
    fix: "Send the reference to support. Nothing on the connection needs to change.",
    action: "copy_reference",
  },
  // GA4 v2.4.1 and Search Console refuse a blank, shared or mismatched service account.
  // #2428: Google Analytics refused a report's dimension, metric or filter field (HTTP 400). Only
  // that report fails; the connector's message, which names the field and any replacement Google
  // suggests, stays connector text that only a contributor reads (#1108).
  report_definition_invalid: {
    label: "Report refused by Google Analytics",
    sentence:
      "Google Analytics did not accept one of this report's dimensions, metrics or filter fields",
    fix: "A contributor can edit the report under Reports on the connection's Configuration tab and check each dimension, metric and filter name.",
    action: "open_connection",
  },
  // #2428: a selected report with no definition (written by an older web): never sent to the
  // image, which would otherwise fall back to a built-in report of the same name.
  report_definition_missing: {
    label: "Report definition missing",
    sentence: "This report has no property, dimensions or metrics saved, so it was not read",
    fix: "Add the report again under Reports on the connection's Configuration tab.",
    action: "open_connection",
  },
  google_service_account_invalid: {
    label: "Check the service account",
    sentence: "The connection's Google service account is missing or not allowed",
    fix: "Enter the service account Skypoint set up for your tenant on the connection, then validate it.",
    action: "validate_connection",
  },
  source_host_key_changed: {
    label: "Host key changed",
    sentence: "The server's host key did not match the one pinned on the connection",
    fix: "Confirm the new host key with the server's owner, then update the connection.",
    action: "open_connection",
  },
  table_permission_denied: {
    label: "No permission on a table",
    sentence: "The source refused to let skyData read a table or view",
    fix: "Grant SELECT on the table or view, and on every table the view reads, including tables in other databases, to the sign-in skyData uses. Then retry the failed tables.",
    action: "retry_failed_tables",
  },
  // #1084: the SQL extractor's pre-flight (`JDBC_PREFLIGHT_VALIDATION_FAILED`) and its DATA default
  // (`JDBC_TABLE_MISSING`) land here by code (`runner/steps/causeFamilies/sqlExtract.ts`). A retry
  // cannot bring a missing table back, so the next step is the source, then the table list.
  table_missing: {
    label: "Table not found",
    sentence: "A selected table or view was not found at the source",
    fix: `Check that the table or view exists in the source. If it was renamed or removed, open the connection's tables, ${VALIDATE_AGAIN_STEP} and update your selection.`,
    action: "open_tables",
  },
  // #1465: a selected SQL Server view fails at read time for reasons of its own. Each has a
  // cause that names the real step instead of "Not recognised" or "Table not found".
  source_cross_database_denied: {
    label: "No access to another database",
    sentence: "The view reads a table in another database that this user cannot access",
    fix: "Give the sign-in skyData uses access to the other database the view reads, then retry the failed tables.",
    action: "retry_failed_tables",
  },
  view_query_failed: {
    label: "View could not be read",
    sentence:
      "The view's own query failed on the source data, or it refers to something that no longer exists",
    fix: "Fix the view in the source, or deselect it on the connection. Then retry the failed tables.",
    action: "open_tables",
  },
  column_type_unsupported: {
    label: "Column type not supported",
    sentence:
      "A column of this table or view has a type skyData cannot read yet (geography, geometry or hierarchyid)",
    fix: "Deselect this table or view on the connection for now. Skypoint is adding support for the type.",
    action: "open_tables",
  },
  // #1084: the column twin of table_missing's next step (the editor has no Refresh control; a
  // validation lists the tables and their columns again).
  column_missing: {
    label: "Column not found",
    sentence: "A selected column was not found at the source",
    fix: `Check that the column exists in the source. If it was renamed or removed, open the connection's tables, ${VALIDATE_AGAIN_STEP} and update your selection.`,
    action: "open_tables",
  },
  // #1465: a view or table whose name the extractor cannot read (an identifier it cannot quote).
  object_name_unreadable: {
    label: "Name not readable",
    sentence: "The name of a selected table or view could not be read by the import",
    fix: "Rename the table or view at the source, or deselect it in the connection's tables. Then retry the failed tables.",
    action: "open_tables",
  },
  file_parse_failed: {
    label: "File not readable",
    sentence: "A source file could not be parsed with the connection's format",
    fix: "Correct or remove the file at the source, or check the format on the connection. Then retry the failed tables.",
    action: "open_connection",
  },
  read_interrupted: {
    label: "Read interrupted",
    sentence: "The read stopped for a reason outside the table",
    fix: RETRY,
    action: "retry_failed_tables",
  },
};

const LOAD_SPECIFIC: Record<Exclude<LoadCauseCode, TextCauseCode>, ImportCauseEntry> = {
  stage_credential_rejected: {
    label: "Staging credential rejected",
    sentence:
      "The storage credential the warehouse reads staged files with was refused or has expired",
    fix: "Replace the staging credential in Destination, then retry the failed tables.",
    action: "open_destination",
  },
  warehouse_login_rejected: {
    label: "Warehouse sign-in refused",
    sentence: "The warehouse refused the key pair or password skyData signs in with",
    fix: "Update the key pair or password in Destination.",
    action: "open_destination",
  },
  warehouse_role_rejected: {
    label: "Warehouse role refused",
    sentence:
      "The Role in Destination does not exist or is not granted to the user skyData signs in as",
    fix: "Check the Role in Destination. If it is right, grant it to the user, then retry the failed tables.",
    action: "open_destination",
    askLine: ASK_SNOWFLAKE_ADMIN,
  },
  warehouse_unavailable: {
    label: "No warehouse available",
    sentence: "The warehouse named in Destination was not available to the session",
    fix: "Check that the warehouse in Destination exists and can resume, and that skyData's role has USAGE and OPERATE on it.",
    action: "open_destination",
    askLine: ASK_SNOWFLAKE_ADMIN,
  },
  warehouse_object_denied: {
    label: "Object missing or not granted",
    sentence: "A database, schema or table was missing or not granted to skyData's role",
    fix: "Grant skyData's role usage on the database and schema, or create them.",
    action: "open_destination",
    askLine: ASK_SNOWFLAKE_ADMIN,
  },
  column_narrowed: {
    label: "Column type narrowed",
    sentence: "The source column changed to a type the target table cannot hold",
    fix: "Re-sync the table to rebuild it, or widen the column in the warehouse.",
    action: "resync_table",
  },
  rows_short: {
    label: "Row count did not match",
    sentence: "The rows loaded did not match what the extract staged",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  // 2026-09-30: the extract's manifest says a table lands one way (`replace_window` over a
  // window, or `replace_all`) and the connector definition declares another, or the window it
  // names is unusable or does not hold the staged rows. The table is left as it was.
  load_mode_mismatch: {
    label: "Load mode mismatch",
    sentence:
      "The connector reported a way of loading this table that its definition does not declare",
    fix: "Send the reference to support. The table was left as it was.",
    action: "copy_reference",
  },
  // Round 6: a table is `<connection name>_<schema>_<table>`, and two selected tables that fold to one
  // name are refused when they are selected (409 `TABLE_NAME_TAKEN`). A run that still holds two
  // fails both before either is touched. (Round 5 used this code for a table found under both its
  // old and new name; the in-place rename that could meet that is gone.)
  table_name_conflict: {
    label: "Two tables for one name",
    sentence:
      "Two selected tables would load into the same warehouse table, so skyData loaded neither",
    fix: "Deselect one of them on the connection, then retry the failed tables.",
    // #1084: the fix is on the connection first; the retry follows it (`fixBeforeRetry`).
    action: "open_tables",
  },
  // #514 (phase 3, ADR 0013): a per-tenant tenant's load pushes its staged files into skyData's
  // own stage in the warehouse first. The push job did not put them all there (`PUSH_FAILED`,
  // `PUSH_SIZE_MISMATCH`); nothing was loaded and the files were kept.
  push_failed: {
    label: "Files not sent to warehouse",
    sentence: "skyData could not send the imported files into its stage in the warehouse",
    fix: RETRY,
    action: "retry_failed_tables",
  },
  // #514: a stage with skyData's push stage name exists in the load schema and points somewhere
  // (`STAGE_KIND_MISMATCH`). skyData never alters a stage it did not create.
  stage_conflict: {
    label: "Stage name already in use",
    sentence:
      "A stage with the name skyData loads through already exists in the load schema and points elsewhere",
    fix: "Drop or rename that stage in the warehouse, then retry the failed tables.",
    action: "open_destination",
    askLine: ASK_SNOWFLAKE_STAGE,
  },
  // #514: a prerequisite of a per-tenant load is missing (`TENANT_SNOWFLAKE_LOAD_NOT_READY` and a
  // closed reason); nothing was loaded and the files were kept.
  load_not_ready: {
    label: "Loading not ready",
    sentence: "This tenant's warehouse loading is not set up yet",
    fix: "Ask Skypoint support to finish this tenant's setup, then retry the failed tables.",
    action: "copy_reference",
  },
  // #924: the per-tenant gate's `binding_incomplete`. A field of the Snowflake destination is
  // empty (`shared/warehouse/snowflakeLoadFields.ts`); the run text names which one. An admin
  // fixes it in a minute, so this is never "Skypoint is looking into it".
  destination_incomplete: {
    label: "Destination incomplete",
    sentence: "The Snowflake destination is missing a field the load needs",
    fix: "A platform admin fills in the missing field in Settings, Destination, then retries the failed tables.",
    action: "open_destination",
  },
  // A table with this table's warehouse name exists, and this connection's own records say it
  // never landed it (`runner/load/tableOwnership.ts`): another connection's, a deleted one's, or
  // the customer's own. The load wrote nothing to it and the rest of the run went on.
  target_not_ours: {
    label: "Not created by this connection",
    sentence:
      "A warehouse table with this name already exists and this connection did not create it, so skyData left it as it was",
    fix: "Choose another warehouse table name for this table on the connection, or remove the existing table in the warehouse. Then retry the failed tables.",
    // The fix is on the connection first; the retry follows it (`fixBeforeRetry`).
    action: "open_tables",
  },
  // Owner 2026-10-31: column names no longer take the `CDM` prefix, and this table still has the
  // old names (`runner/load/oldColumnNames.ts`). A full load rebuilds such a table in place with
  // the new names (grants kept), so only a load that does not read it whole is refused, with
  // nothing written; the fix is that full read. The table's error names one old column and says
  // no next step (`oldColumnNamesSentence`), so the next step is said once, here.
  old_column_names: {
    label: "Old column names",
    sentence: "This table still has the old CDM_ column names, so skyData left it as it was",
    fix: "Run a full refresh: on the connection, choose Import with options, then Full refresh. The table is rebuilt with the new column names.",
    action: "full_refresh",
  },
};

const PLATFORM: Record<PlatformCauseCode, ImportCauseEntry> = {
  connection_not_ready: {
    label: "Connection not ready",
    sentence: "The connection was not ready to import",
    fix: "Validate the connection and select at least one table.",
    action: "validate_connection",
  },
  job_not_started: {
    label: "Import did not start",
    sentence: "skyData could not start the import job",
    fix: "Send the reference to support.",
    action: "copy_reference",
  },
  run_timed_out: {
    label: "Time limit reached",
    sentence: "The import ran past its time limit and was stopped",
    fix: "Split the connection's tables across two schedules, or ask for a longer limit. Retry the failed tables.",
    action: "retry_failed_tables",
  },
  job_lost: {
    label: "Job lost",
    sentence: "The import job stopped reporting and could not be found",
    fix: "Retry the failed tables.",
    action: "retry_failed_tables",
  },
  run_overlapped: {
    label: "Another import running",
    sentence: "The source reported another import of this connection already running",
    fix: "Retry the failed tables once the other import finishes.",
    action: "retry_failed_tables",
  },
  // Round 8: a target field changed after the connection's last test, so the stored credential
  // was not sent to the new target (`shared/connectors/targetFields.ts`).
  connection_untested: {
    label: "Connection not tested",
    sentence:
      "The connection's target changed after its last connection test, so skyData did not use the stored credentials",
    fix: "Test the connection, then run it again.",
    action: "validate_connection",
  },
  // F32: the tenant was disabled while the run was in flight; the runner cancelled it
  // (`runner/tenancy/disabledTenantRuns.ts`, `runner/tenancy/tenantDisabledStop.ts`).
  tenant_disabled: {
    label: "Tenant disabled",
    sentence: "The run stopped because the tenant was disabled",
    fix: "Ask Skypoint support to enable the tenant, then run it again.",
    action: "copy_reference",
  },
  // #787: a per-tenant tenant whose job identity is not ready yet (ADR 0010; the provisioner is
  // still setting it up), so the extract was refused before anything ran.
  tenant_not_ready: {
    label: "Tenant setup not finished",
    sentence: "This tenant's resources are still being set up, so the import did not start",
    fix: "Skypoint is finishing this tenant's setup. Import again once it is done. Ask Skypoint support if it takes more than an hour.",
    action: "copy_reference",
  },
};

/**
 * The step error the runner records for a step that ended while its tenant was disabled, in
 * place of the job's own failure (a 403 from the vault or storage, a stopped execution). The
 * technical detail stays in the job's console, which Technical details reads.
 */
export const TENANT_DISABLED_STEP_ERROR = "Stopped because the tenant was disabled.";

/** The whole catalogue, by source. A code added to a list without an entry fails `npm run check`. */
export const IMPORT_CAUSES = {
  connector: { ...CONNECTOR_TEXT, ...EXTRACT_SPECIFIC },
  load: { ...LOAD_TEXT, ...LOAD_SPECIFIC },
  platform: PLATFORM,
} as const satisfies {
  connector: Record<ExtractCauseCode, ImportCauseEntry>;
  load: Record<LoadCauseCode, ImportCauseEntry>;
  platform: Record<PlatformCauseCode, ImportCauseEntry>;
};

/**
 * #787: the causes that mean a readiness gate refused the import, not that it broke: the
 * connection is fine and nothing is wrong with the source, a prerequisite of the tenant or the
 * destination is not in place yet. A connection whose last run failed ONLY with these reads
 * **Waiting** (with the cause's sentence and fix), never Failed and never Active. Add a gate's
 * code here when it is born (A2's destination check, for one).
 */
export const WAITING_CAUSES: readonly ImportCauseKey[] = [
  "load:load_not_ready",
  "load:destination_incomplete",
  "platform:tenant_not_ready",
];

/** `source:code`, the one spelling `WAITING_CAUSES` uses. */
export type ImportCauseKey =
  `connector:${ExtractCauseCode}` | `load:${LoadCauseCode}` | `platform:${PlatformCauseCode}`;

/** True when a stored cause is a readiness gate's refusal (`WAITING_CAUSES`). */
export function isWaitingCause(cause: ImportCause | null): boolean {
  if (!cause) return false;
  return WAITING_CAUSES.includes(`${cause.source}:${cause.code}` as ImportCauseKey);
}

/** A stored import cause: `run_steps.metrics.importCause`, a notification's `facts.cause`. */
export const importCauseSchema = z.discriminatedUnion("source", [
  z.object({ source: z.literal("connector"), code: z.enum(EXTRACT_CAUSE_CODES) }).strict(),
  z.object({ source: z.literal("load"), code: z.enum(LOAD_CAUSE_CODES) }).strict(),
  z.object({ source: z.literal("platform"), code: z.enum(PLATFORM_CAUSE_CODES) }).strict(),
]);
export type ImportCause = z.infer<typeof importCauseSchema>;

/** The catalogue entry of a stored cause. */
export function importCauseEntry(cause: ImportCause): ImportCauseEntry {
  switch (cause.source) {
    case "connector":
      return IMPORT_CAUSES.connector[cause.code];
    case "load":
      return IMPORT_CAUSES.load[cause.code];
    case "platform":
      return IMPORT_CAUSES.platform[cause.code];
  }
}

/** A value read from a jsonb column, as a cause; null for anything else (an old row, a bad key). */
export function parseImportCause(value: unknown): ImportCause | null {
  if (value === null || typeof value !== "object") return null;
  const parsed = importCauseSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * #1108 (owner, 2026-10-15, option 1): the key that reads a connector's own error text, as the
 * runner's deny-list left it (`sanitiseConnectorError`). Contributor and above hold it, because
 * they fix connections. A reader without it (a Viewer) reads `causeOnlyErrorText` in its place,
 * on the run page, in the live frames, in the event log download and on the Overview.
 */
export const CONNECTOR_ERROR_TEXT_PERMISSION = "dataflows.write" satisfies PermissionKey;

/** What a cause-only reader is told after the cause's label. */
export const ASK_A_CONTRIBUTOR = "Ask a contributor for the details.";

const UNKNOWN_CONNECTOR_CAUSE: ImportCause = { source: "connector", code: "unknown" };

/**
 * #1108: a connector error as a reader without `CONNECTOR_ERROR_TEXT_PERMISSION` reads it: the
 * cause's fixed label and `ASK_A_CONTRIBUTOR`, never a word of the source's text. With no stored
 * cause, the label of `unknown` ("Not recognised"). The code travels beside it (a step's or a
 * table's `cause.code`, an event's `causeCode`).
 */
export function causeOnlyErrorText(cause: ImportCause | null): string {
  return `${importCauseEntry(cause ?? UNKNOWN_CONNECTOR_CAUSE).label}. ${ASK_A_CONTRIBUTOR}`;
}

/**
 * #1108 (security review of #1315): the runner's own fixed step sentences, whole, the only text
 * a cause-only reader keeps under a cause that is not the connector's. Matched on the whole text,
 * never on the cause's source: a platform cause can sit on a step whose error is the connector's
 * own summary (a container that published an error then exited 2 with no exit report). Only
 * sentences with no free-text slot (a number, an execution name's own characters): the two
 * "Connection <name> ..." sentences are left out, so a source message shaped like them reads the
 * cause's sentence (security review of #1315).
 */
const SKYDATA_STEP_SENTENCES: readonly RegExp[] = [
  /^Stopped because the tenant was disabled\.$/,
  /^Extract exceeded its \d+s budget$/,
  /^The execution [A-Za-z0-9-]{1,100} could not be found on \d+ consecutive polls; the container is gone$/,
];

/** True for one of the runner's own fixed step sentences (`SKYDATA_STEP_SENTENCES`). */
export function isSkydataStepSentence(text: string): boolean {
  return SKYDATA_STEP_SENTENCES.some((pattern) => pattern.test(text));
}

/**
 * #1108: a run error under a cause that is not the connector's (`platform`, `load`), as a
 * cause-only reader reads it: the runner's own fixed sentence as it is, any other text as the
 * cause's fixed sentence (its entry's `sentence`). No "ask a contributor" line: the fix beside it
 * says who acts.
 */
export function causeOnlyOwnErrorText(text: string, cause: ImportCause): string {
  return isSkydataStepSentence(text) ? text : `${importCauseEntry(cause).sentence}.`;
}

/**
 * #1108: the cause a connector's event payload names: its `causeSource` and `causeCode` (the
 * drain's classified cause), else its text category (`errorCategory`) on the connector's list,
 * else null.
 */
export function connectorPayloadCause(payload: Record<string, unknown>): ImportCause | null {
  return (
    parseImportCause({ source: payload.causeSource, code: payload.causeCode }) ??
    parseImportCause({ source: "connector", code: payload.errorCategory })
  );
}

/** True when `code` is a code of `source`'s list. */
export function isImportCauseCode(source: ImportCauseSource, code: unknown): boolean {
  return parseImportCause({ source, code }) !== null;
}

/**
 * The firewall half of a fix (owner answer W3-1): the platform's outbound address when the web read
 * it from Azure, else the support sentence. Empty also covers a set too large for a customer to
 * allow (`outboundAddresses.ts`, `too_many`): the web never passes part of a list. `null` means
 * "this process does not know" (the runner, which renders notifications and never reads the
 * address): the run page is named instead, and it shows either the addresses or whom to ask.
 */
export function firewallSentence(addresses: readonly string[] | null): string {
  if (addresses === null) {
    return "The run page says which address to allow on the source firewall, or who to ask.";
  }
  if (addresses.length === 0) {
    return "Ask Skypoint support for the address to allow on the source firewall.";
  }
  if (addresses.length === 1) return `Allow ${addresses[0]} on the source firewall.`;
  return `Allow skyData's ${addresses.length} outbound addresses (listed below) on the source firewall.`;
}

/** The whole fix line for a cause, the firewall half included when the entry needs it. */
export function importCauseFix(cause: ImportCause, addresses: readonly string[] | null): string {
  const entry = importCauseEntry(cause);
  return entry.firewall ? `${entry.fix} ${firewallSentence(addresses)}` : entry.fix;
}

/**
 * #1465: where table-level runs are off (`tableRunsEnabled`) there is no "Retry the failed tables"
 * button or route, only the run-level retry, so the catalogue's words say the run.
 */
export function runLevelRetryWords(text: string): string {
  return text.replace(
    /\b(retr(?:y|ies)) the failed tables\b/gi,
    (_match, verb: string) => `${verb} the run`,
  );
}

/** The most tables `run_steps.metrics.tableCauses.tables` names (the rest are in the counts). */
export const MAX_TABLE_CAUSES = 50;

/**
 * Per-table causes of one step: how many tables failed with each code, and the first
 * `MAX_TABLE_CAUSES` tables by name (SEC-17: entity names and codes only).
 */
export interface TableCauses {
  counts: Record<string, number>;
  tables: Array<{ entityName: string; code: string }>;
}

/** Builds `tableCauses` from per-table codes, bounded. */
export function tableCausesOf(
  rows: ReadonlyArray<{ entityName: string; code: string }>,
): TableCauses {
  const counts: Record<string, number> = {};
  for (const row of rows) counts[row.code] = (counts[row.code] ?? 0) + 1;
  return {
    counts,
    tables: rows.slice(0, MAX_TABLE_CAUSES).map(({ entityName, code }) => ({ entityName, code })),
  };
}

/** The code most tables share (ties: the first seen), or null for none. */
export function dominantCode(counts: Record<string, number>): string | null {
  let best: string | null = null;
  let bestCount = 0;
  for (const [code, count] of Object.entries(counts)) {
    if (count > bestCount) {
      best = code;
      bestCount = count;
    }
  }
  return best;
}

/** A `tableCauses` value read from jsonb; null when it has not that shape. */
export function parseTableCauses(value: unknown): TableCauses | null {
  if (value === null || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const counts = record.counts;
  const tables = record.tables;
  if (counts === null || typeof counts !== "object" || !Array.isArray(tables)) return null;
  const outCounts: Record<string, number> = {};
  for (const [code, count] of Object.entries(counts as Record<string, unknown>)) {
    if (typeof count === "number" && Number.isFinite(count) && count > 0) outCounts[code] = count;
  }
  const outTables = tables.flatMap((row) => {
    if (row === null || typeof row !== "object") return [];
    const { entityName, code } = row as Record<string, unknown>;
    return typeof entityName === "string" && typeof code === "string" ? [{ entityName, code }] : [];
  });
  return { counts: outCounts, tables: outTables };
}

/**
 * What `GET /runs/:runId` returns for a cause (`RunStepRow.cause`, `EntityProgressRow.cause`): the
 * server's words for a stored code. The client renders it and never composes a sentence.
 */
export interface ImportCauseView {
  source: ImportCauseSource;
  code: string;
  label: string;
  sentence: string;
  /** The fix line, the firewall address included where it applies. */
  fix: string;
  action: ImportCauseAction;
  actionLabel: string;
  /** The key the action needs; null for an action anyone can take (copy the reference). */
  permission: PermissionKey | null;
  /** What a reader without `permission` reads instead of the button; null when nothing. */
  withoutPermission: string | null;
  /** Tables of this step that failed with this code; absent for a step-level cause. */
  tables?: number;
  /** The platform's outbound addresses, on a firewall cause the web could read them for. */
  addresses?: string[];
}
